import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-brand text-white shadow-xs hover:bg-brand-hover active:bg-brand-active ' +
    'dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover',
  secondary:
    'bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 active:bg-zinc-100 ' +
    'dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800 dark:active:bg-zinc-800',
  ghost:
    'text-zinc-700 hover:bg-zinc-100 active:bg-zinc-200 ' +
    'dark:text-zinc-200 dark:hover:bg-zinc-800 dark:active:bg-zinc-700',
  destructive:
    'bg-rose-600 text-white shadow-xs hover:bg-rose-700 active:bg-rose-800 ' +
    'dark:bg-rose-600 dark:hover:bg-rose-500',
};

const SIZES: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2.5 text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-md transition',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
        'dark:focus-visible:ring-brand-dm dark:focus-visible:ring-offset-zinc-950',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
}
